import React, { useState } from "react";
import Layout from "../../Components/Layout";
import Ribbon from "./Ribbon";
import { Category, EntityFieldsMap, EntityFormFields, categoryLabels } from "./Categories";
import { DataTable } from "../../Components/DataTable";
import { AddButton, Button } from "../../Components/Buttons";
import AddGenericModal from "../../Modals/AddGenericModal";
import DeleteConfirmationModal from "../../Modals/DeleteConfirmationalModal";
import { ErrorSuccessModal } from "../../Modals/ErrorSuccessModal";
import { useTableColumns } from "./TableConfig";
import { useEntityAdapter } from "./useEntityAdapter";
import { entityIdKeyMap } from "../../EntityKeys";
import { Gender, GenderLabels } from "../../constants";

const EntityManagementPage = () => {
  const [category, setCategory] = useState<Category>("patient");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [editingId, setEditingId] = useState<number | null>(null);

  // 1. Παίρνουμε τον adapter στην κορυφή
  const adapter = useEntityAdapter(category);
  const safeData = adapter.data || [];

  // 2. Τα ονόματα των πεδίων της φόρμας
  const formFieldNames = EntityFormFields[category] || [];

  // 3. Αν ο adapter έχει έτοιμα fields (π.χ. για τον γιατρό με τις ειδικότητες), πάρε αυτά. Αλλιώς από το EntityFieldsMap.
  const allFields = adapter.fields && adapter.fields.length > 0 
    ? adapter.fields 
    : (EntityFieldsMap[category] || []);

  // 4. Φιλτράρισμα πεδίων
  const currentFields = allFields.filter((field) => formFieldNames.includes(field.name));
  const categoryLabel = categoryLabels[category] || category;
  const columns = useTableColumns({
    data: safeData,
    category,
    fields: currentFields,
    selectedIds,
    setSelectedIds,
    handleRowClick: (item) => {
      const idKey = entityIdKeyMap[category as keyof typeof entityIdKeyMap] || "id";
      const itemId = item[idKey];

      if (itemId != null) {
        setEditingId(Number(itemId));
        setFormData(item);
        setIsModalOpen(true);
      } else {
        console.error(
          `Σφάλμα: Δεν βρέθηκε το πεδίο "${idKey}" στα δεδομένα της κατηγορίας "${category}":`,
          item
        );
      }
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 1. Φτιάχνουμε τα δεδομένα με κεφαλαία (PascalCase) χωρίς wrapper
    const formattedData: Record<string, any> = {};
    Object.keys(formData).forEach((key) => {
      const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);
      formattedData[capitalizedKey] = key === "memberOfMedicalTeam" 
        ? Boolean(formData[key]) 
        : formData[key];
    });

    // 2. Στέλνουμε απευθείας τα formattedData (χωρίς { dto: ... })
    if (editingId != null && adapter.update) {
      await adapter.update(editingId, formattedData as any);
    } else {
      await adapter.create(formattedData as any);
    }

    setIsModalOpen(false);
    setFormData({});
    setEditingId(null);
  };

  const handleConfirmDelete = async () => {
    await adapter.remove(selectedIds as any);
    setIsDeleteModalOpen(false);
    setSelectedIds([]);
  };

  const handleOpenAddModal = () => {
    setEditingId(null);
    setFormData({});
    setIsModalOpen(true);
  };

  return (
    <Layout>
      <div className="flex flex-col h-full min-w-0 p-6 space-y-6 overflow-x-auto">
        
        <Ribbon activeCategory={category} onChange={setCategory} />

        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-bold text-slate-800 capitalize"></h2>
            <AddButton label="Νέα Εγγραφή" onClick={handleOpenAddModal} />
          </div>

          <DataTable columns={columns} data={safeData} loading={adapter.isLoading} />

          <Button
            variant="danger"
            disabled={selectedIds.length === 0}
            onClick={() => setIsDeleteModalOpen(true)}
          >
            Διαγραφή επιλεγμένων ({selectedIds.length})
          </Button>
        </div>

        {/* Modal για Create / Edit */}
        <AddGenericModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setEditingId(null);
            setFormData({});
          }}
          title={
            editingId != null
              ? `Επεξεργασία (${categoryLabel})`
              : `Νέα Εγγραφή (${categoryLabel})`
          }
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            {currentFields && currentFields.length > 0 ? (
              <div className="space-y-3">
                {currentFields.map((field) => {
                  const fieldName = field.name;
                  const fieldLabel = field.label;
                  const isGenderField = fieldName.toLowerCase() === "gender";
                  const isSelectField = field.type === "select" || isGenderField;

                  return (
                    <div key={fieldName} className="flex flex-col gap-1">
                      <label className="text-sm font-medium text-slate-700">
                        {fieldLabel}
                      </label>

                      {isSelectField ? (
                        <select
                          className="border rounded px-3 py-2 text-sm bg-white"
                          value={formData[fieldName] ?? ""}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              [fieldName]: isGenderField ? Number(e.target.value) : e.target.value,
                            })
                          }
                        >
                          <option value="">Επιλέξτε...</option>
                          
                          {/* Αν είναι το πεδίο φύλου, παίρνουμε τα options από τα GenderLabels */}
                          {isGenderField &&
                            Object.entries(GenderLabels).map(([key, label]) => (
                              <option key={key} value={key}>
                                {label}
                              </option>
                            ))}

                          {/* Αν είναι κανονικό select πεδίο (όπως η ειδικότητα του γιατρού), παίρνει τα options του */}
                          {!isGenderField &&
                            field.options?.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                        </select>
                      ) : (
                        <input
                          type={field.type || "text"}
                          className="border rounded px-3 py-2 text-sm"
                          value={formData[fieldName] ?? ""}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              [fieldName]: e.target.value,
                            })
                          }
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-sm text-slate-500">
                Δεν έχουν οριστεί πεδία για αυτή την κατηγορία στο EntityFieldsMap.
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setIsModalOpen(false);
                  setEditingId(null);
                  setFormData({});
                }}
              >
                Ακύρωση
              </Button>
              <Button
                type="submit"
                variant="primary"
                isLoading={adapter.isLoading}
              >
                {editingId != null ? "Ενημέρωση" : "Αποθήκευση"}
              </Button>
            </div>
          </form>
        </AddGenericModal>

        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onConfirm={handleConfirmDelete}
          onCancel={() => setIsDeleteModalOpen(false)}
        />

        <ErrorSuccessModal
          isOpen={adapter.modalState.isOpen}
          type={adapter.modalState.type}
          title={adapter.modalState.title}
          message={adapter.modalState.message}
          onClose={adapter.closeModal}
        />
      </div>
    </Layout>
  );
};

export default EntityManagementPage;